import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';

// Utils
import { downloadFile } from 'utils/downloadFile';
import { getClassifierText, t } from 'utils/getTranslations';
import { useGoBack, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useConfirmDeleteContract } from './useConfirmDeleteContract';
import { getAppliedInOffice } from './util';

// Components
import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import Tooltip from 'components/@extended/Tooltip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import PinButton from 'components/buttons/PinButton';
import CircularLoader from 'components/CircularLoader';
import DmsLink from 'components/dmslink/DmsLink.tsx';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import type { DraftActions } from 'components/drafts/useDraftActions';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import PageHeader from 'components/PageHeader';
import { RecordMoreMenu } from 'components/record-header/RecordHeader';
import { Button } from 'components/ui/button';
import { PricelistDetailsCard } from 'pages/pricelists/PricelistDetailsCard';

// Types
import { ClassifierData } from 'types/classifiers';
import { Contract, ContractStatusType, ContractType } from 'types/contracts';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { recordPin } from 'utils/pins';

interface Props {
    contract: Contract;
    classifiers: Record<string, ClassifierData>;
    /** A draft's own actions, and the draft once loaded: they replace the contract's, and its pin goes. */
    draftActions?: DraftActions;
}

export const ContractPageHeader = ({ contract, classifiers, draftActions }: Props): JSX.Element => {
    const isNew = useIsNewDesign();
    const navigateWithReturn = useNavigateWithReturnState();
    const confirmDelete = useConfirmDeleteContract(String(contract.id));
    const goBack = useGoBack('/contracts');
    const isAmlOrUbo = contract.contractTypeCode === ContractType.AML || contract.contractTypeCode === ContractType.UBO;
    const isRepresentation = contract.contractTypeCode === ContractType.REPRESENTATION;

    const statusChipType = useMemo<ChipType>(() => {
        switch (contract.contractStatusTypeCode) {
            case ContractStatusType.DRAFT:
                return 'outlined';
            case ContractStatusType.VALID:
                return 'success';
            case ContractStatusType.TERMINATED:
                return 'inactive';
        }
    }, [contract.contractStatusTypeCode]);

    const [isDownloadingContract, setIsDownloadingContract] = useState<boolean>(false);
    return (
        <PageHeader
            title={contract.contractName}
            showBreadcrumb
            onBack={goBack}
            buttons={
                draftActions && contract.isDraft ? (
                    <div className="flex items-center justify-end gap-2">
                        <DraftRecordActions actions={draftActions} />
                    </div>
                ) : (
                    <div className="flex justify-end gap-2">
                        <FeatureFlagged action="show" flag={FeatureFlag.DOWNLOAD_CONTRACT_TEMPLATE}>
                            {/* The tooltip hangs on the wrapper: the disabled button itself takes no pointer events. */}
                            <Tooltip
                                text={contract.contractTemplate ? undefined : 'contracts.tooltips.template-required'}
                            >
                                <span>
                                    <Button
                                        {...buttonStyle({ variant: 'outlined', size: 'small' }, 'relative')}
                                        onClick={() => {
                                            setIsDownloadingContract(true);
                                            // No filename argument: the backend resolves it from the template's
                                            // configured file name format, falling back to the contract name.
                                            downloadFile(`/v1/contracts/getPrefilledDocument/${contract.id}`).finally(
                                                () => setIsDownloadingContract(false)
                                            );
                                        }}
                                        disabled={!contract.contractTemplate || isDownloadingContract}
                                    >
                                        {/* Hidden rather than removed while loading, so the button keeps its width. */}
                                        <span className={cn(isDownloadingContract && 'invisible')}>
                                            {t('download')}
                                        </span>
                                        {isDownloadingContract ? (
                                            <CircularLoader className="z-auto [&>span]:size-4 [&>span]:border-2" />
                                        ) : null}
                                    </Button>
                                </span>
                            </Tooltip>
                        </FeatureFlagged>
                        {/* The new design edits each section in place, so Delete moves here from the edit form. */}
                        {!isNew ? (
                            <PermissionButton
                                permissions={['UPDATE.CONTRACTS.PARTY_ACCESS']}
                                variant="outlined"
                                size="small"
                                onClick={() => navigateWithReturn(`/contract/${contract.id}/edit`)}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        ) : (
                            <RecordMoreMenu
                                menuItems={
                                    contract.isDeletable
                                        ? [{ handler: confirmDelete, title: t('delete'), variant: 'destructive' }]
                                        : []
                                }
                            />
                        )}
                    </div>
                )
            }
        >
            <div className="flex flex-wrap items-center gap-2 py-4 sm:gap-4">
                <div className="flex gap-2">
                    {contract.isDraft ? <DraftChip /> : null}
                    <StatusChip
                        type={statusChipType}
                        label={getClassifierText(classifiers['CONTRACT_STATUS_TYPE'], contract.contractStatusTypeCode)}
                    />
                    {contract?.dmsLink ? <DmsLink link={contract?.dmsLink} /> : null}
                    {isNew && !contract.isDraft ? (
                        <PinButton pin={recordPin('contract', contract.id, contract.contractName)} />
                    ) : null}
                </div>
                <ContractMeta contract={contract} classifiers={classifiers}>
                    {!isAmlOrUbo && !isRepresentation ? (
                        <div className="flex">
                            <div className="whitespace-nowrap">
                                <FormattedMessage id="contracts.pricelist" />
                            </div>
                            :&nbsp;
                            <PricelistDetailsCard pricelist={contract.pricelist} simpleView />
                        </div>
                    ) : null}
                    {draftActions?.draft ? <DraftMeta draft={draftActions.draft} /> : null}
                </ContractMeta>
            </div>
        </PageHeader>
    );
};

/** The classic header's meta line: the contract's type and the office it applies in, then the caller's own items. */
export function ContractMeta({
    contract,
    classifiers,
    children,
}: {
    contract: Contract;
    classifiers: Record<string, ClassifierData>;
    children?: ReactNode;
}) {
    return (
        <div className="flex flex-wrap gap-2 text-xs leading-5 font-medium">
            <div className="flex">
                <div>
                    <FormattedMessage id="contracts.type" />
                </div>
                :&nbsp;<div>{getClassifierText(classifiers['CONTRACT_TYPE'], contract.contractTypeCode)}</div>
            </div>
            <div className="flex">
                <div className="whitespace-nowrap">
                    <FormattedMessage id="contracts.applied-in" />
                </div>
                :&nbsp;<div>{getAppliedInOffice(contract)}</div>
            </div>
            {children}
        </div>
    );
}
